import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Container,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
  IconButton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { VideoCard } from '../Video/VideoCard';
import Masonry from '@mui/lab/Masonry';
import { fetchSearch, VideoItem } from '../../api/serachService';
import { VideoCardSkeleton } from '../Video/VideoCardSkeleton';

const SORT_OPTIONS = [
  { value: 'newest', label: 'Сначала новые' },
  { value: 'popular', label: 'По популярности' },
  { value: 'likes', label: 'По лайкам' },
];

const useDebounce = (callback: (value: string) => void, delay = 1000) => {
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const debouncedCallback = useCallback((value: string) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    
    timerRef.current = setTimeout(() => {
      callback(value);
    }, delay);
  }, [callback, delay]);

  // Очистка при размонтировании
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return debouncedCallback;
};

export const SearchPage = () => {
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('newest');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const limit = 12;

  const loadVideos = useCallback(async () => {
    setLoading(true);
    try {
      if (searchQuery.trim()) {
        const result = await fetchSearch(searchQuery, (page - 1) * limit, limit, sort);
        setIsSearching(true);
        setVideos(result.data);
        setTotal(result.total);
      } else {
        setIsSearching(false);
        setVideos([]);
        setTotal(0);
      }
    } finally {
      setLoading(false);
    }
  }, [page, sort, searchQuery]);

  // Дебаунс для поиска
  const debouncedLoadVideos = useDebounce((query: string) => {
    setSearchQuery(query);
    setPage(1); // Сбрасываем на первую страницу при новом поиске
  }, 1000);

  useEffect(() => {
    loadVideos();
  }, [loadVideos]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value); // Обновляем сразу для отзывчивости UI
    debouncedLoadVideos(value); // Дебаунс для запроса
  };

  const clearSearch = () => {
    setSearchQuery('');
    setPage(1);
  };

  return (
    <Container maxWidth={false} sx={{ py: 4 }}>
      {/* Поиск и фильтры */}
      <Box sx={{
        mb: 4,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2,
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' }
      }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Поиск видео..."
          value={searchQuery}
          onChange={handleSearchChange}
          sx={{
            maxWidth: { sm: 500 },
            '& .MuiOutlinedInput-root': {
              borderRadius: 4,
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton onClick={clearSearch} edge="end">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Сортировка</InputLabel>
          <Select
            value={sort}
            label="Сортировка"
            onChange={(e) => setSort(e.target.value)}
          >
            {SORT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Результаты поиска */}
      {isSearching && searchQuery && (
        <Box sx={{ mb: 3 }}>
          <h3>Результаты поиска по запросу: "{searchQuery}"</h3>
        </Box>
      )}

      {/* Мозаичная сетка видео */}
      {loading ? (
        <Masonry columns={{ xs: 1, sm: 2, md: 3, lg: 4 }} spacing={3}>
          {Array.from({ length: limit }).map((_, index) => (
            <VideoCardSkeleton key={index} />
          ))}
        </Masonry>
      ) : (
        <>
          {videos.length > 0 ? (
            <Masonry columns={{ xs: 1, sm: 2, md: 3, lg: 4 }} spacing={3}>
              {videos.map((video) => (
                <div key={video.id} className="flip-card">
                  <VideoCard video={video} />
                </div>
              ))}
            </Masonry>
          ) : (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 200
            }}>
              <h3>{searchQuery ? 'Ничего не найдено' : 'Введите поисковый запрос'}</h3>
            </Box>
          )}
        </>
      )}

      {/* Пагинация */}
      {total > limit && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={Math.ceil(total / limit)}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            size="large"
          />
        </Box>
      )}
    </Container>
  );
};